import React, { Fragment } from 'react';
import dlv from 'dlv';
import Box from '../../layout/box';
import Button from '../../button';
import Menu from '../../menu';
import { isCompositeType } from '../types';
import FieldActions, { getFieldActions } from '../fieldactions';
import { callWith, exists } from '../../../../util';
import cx from 'classnames';
import { MoreVert } from '../../icons';
import { makeStyles } from '../../../theme';
import { editorForType } from './editors';
import ObjZoomOut from './obj-zoom/out';
import { dataFieldKeyToDataDescFieldKey, getDataDescActions } from './util';

const styles = makeStyles(
	({ spacing }) => ({
		addFieldBox: {
			flexDirection:"row",
			justifyContent:"flex-end",
			paddingRight: spacing(2),
			paddingTop: spacing(2),
		},
		deleteFieldBox: {
			width:"100%",
			justifyContent:"flex-end",
			alignItems:"center",
		},
		alignDeleteTop: {
			alignItems:"start"
		},
		deleteFieldChild: {
			width:"100%"
		}
	})
);

function getTemplateDataForField(t) {
	return exists(t) ? (
		Array.isArray(t) ? (
			t.length === 1 ? t[0] : t
		) : (
			exists(t.plainValue) ? t.plainValue : t
		)
	): null;
}

function FieldActionsBox({children, actions = [], onFieldActionRequest, alignTop = false}) {
	const classes = styles();
	return (
		<Box type="row" className={cx(classes.deleteFieldBox, alignTop && classes.alignDeleteTop)}>
			{React.cloneElement(React.Children.only(children), {className:cx(classes.deleteFieldChild)})}
			<Menu id="optionsMenu" icon={MoreVert} items={actions} onSelect={onFieldActionRequest}/>
		</Box>
	);
}

const AddFieldBox = React.memo(({onClick}) => {
	const classes = styles();
	return (
		<Box className={cx(classes.addFieldBox)}>
			<Button icon="Add" onClick={onClick}/>
		</Box>
	);
});

const EditableField = React.memo(
	({ field, displayLabel, dataPath, parentType, onFieldActionRequest, onObjZoomRequest, dataDescriptor }) => {
		const Comp = editorForType(field.type);
		const dPath = `${dataPath ?`${dataPath}.` : ''}${field.key}`;
		const dDesc = dlv(dataDescriptor, dataFieldKeyToDataDescFieldKey(field.key));

		if(Comp){
			const handleFieldActionRequest = (action, args) => {
				if(onFieldActionRequest)onFieldActionRequest(action, dPath, parentType, args);
			};

			return (
				<FieldActionsBox
					actions={getFieldActions(parentType, getDataDescActions(dDesc))}
					onFieldActionRequest={handleFieldActionRequest} 
					alignTop={isCompositeType(field.type)}
					>
					<Comp
						label={displayLabel && field.key} 
						value={field.value}
						onFieldActionRequest={onFieldActionRequest}
						dataPath={dPath}
						parentType={parentType}
						onObjZoomRequest={onObjZoomRequest}
						dataDescriptor={dDesc}
						/>
				</FieldActionsBox>
			)
		}

		return null;
	}
);

const EditableFields = React.memo(
	({ fields = [], displayLabel=true, parentType, dataPath, onFieldActionRequest, objZoomed, onObjZoomRequest, onObjZoomOut, dataDescriptor }) => {
		const {
			allowCustom: allowAddCustomField = true,
			template: templatesForFields = null
		} = dlv(getDataDescActions(dataDescriptor), 'add', {});

		const templateData = getTemplateDataForField(templatesForFields);
		const injectDataDirectly = !allowAddCustomField && exists(templateData) && !Array.isArray(templateData);

		return (
			<Fragment>
				{objZoomed && <ObjZoomOut dataPath={dataPath} onObjZoomOut={onObjZoomOut} />}
				{fields.map((field, i) => (
					<EditableField 
						key={`field-${i}`} 
						displayLabel={displayLabel} 
						field={field} 
						parentType={parentType} 
						dataPath={dataPath} 
						onFieldActionRequest={onFieldActionRequest}
						onObjZoomRequest={onObjZoomRequest}
						dataDescriptor={dataDescriptor}
					/>
				))}
				{(allowAddCustomField || exists(templateData)) && (
					<AddFieldBox onClick={callWith(
						onFieldActionRequest,
						FieldActions.Add,
						dataPath,
						parentType,
						{
							allowAddCustomField,
							injectDataDirectly,
							...injectDataDirectly && templateData.field && {
								fieldName: templateData.field
							},
							fieldValue: injectDataDirectly && exists(templateData.data) ? templateData.data : templateData
						}
					)} />
				)}
			</Fragment>
		);
	}
);

export default EditableFields;